import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { FlexRow, FlexColumn } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { Pill } from 'ui-components/components/pill';

import { USD } from '../../../../../constants/currency-codes';
import { Features } from '../../../../../constants/features';
import { useBuildAddOnFeatures } from '../../../../../hooks/add-ons/use-build-addons';
import { isBillingPlanIdUsageBilledHighVolume } from '../../../../../enums/billing-plan-ids';
import {
  getNonWalletAccessDisplayPrice,
  useIsNonWalletAccessVisible,
  useIsSellerOnNonWalletHighVolumeUsageBasedBillingPlan,
  useGetPerLabelFeeForNonWalletAccessIncludedPlan,
  useNonWalletAccessBillingState,
  useNonWalletAccessDescription,
  NonWalletAccessBillingState,
  getBasePlanForNonWalletAccessPlan,
} from '../../../../../hooks/non-wallet-access-hooks';
import FormattedCurrencyContainer from '../../../../../components/formatted-currency/container';
import UserDateTimezoneFormat from '../../../../../components/user-date-timezone-format';

import {
  useGetDefaultZeroCost,
  useGetPlanPrice,
  useGetPendingSubscriptionChangeData,
  useShipmentLimitCostForBillingPlan,
  useUserLimitData,
} from '../../hooks/use-plan-details';
import { getCurrentLineItems } from '../../utils/plan-utilities';
import { useGetUserSeatsAddOnData } from '../../hooks/use-get-user-seats-addon-data';
import { SubscriptionCardAnalyticsLicenseInfo } from './analytics-license-info';
import { SubscriptionCardPlanInfo } from './plan-info';
import { SubscriptionCardUserInfo } from './user-info';
import SubscriptionCardActions from './actions';
import { SubscriptionCardAddOnsAndFeatures } from './addons-and-features-section';
import {
  BASE_PLAN_LINE_ITEM_ID,
  SHIPMENT_LIMIT_LINE_ITEM_ID,
  USER_COST_LINE_ITEM_ID,
  YOUR_CARRIER_COST_LINE_ITEM_ID,
  ANALYTICS_LICENSE_COST_LINE_ITEM_ID,
} from './constants';
import { PlanTotalCost } from './plan-total-cost';
import { PlanLineItemCost } from './plan-line-item-cost';
import { getFractionalCentsDigits } from '../../../../../services/decimal-display-service';
import { useAnalyticsLicenseData } from '../../hooks/use-analytics-license-data';

import messages from './messages';
import styles from './style.module.less';
import Global from '../../../../../store/global-selectors';

const SubscriptionCard = ({ reactivatePlan }) => {
  const isByoaVisible = useIsNonWalletAccessVisible();
  const isByoaIncludedInSellersPlan = useIsSellerOnNonWalletHighVolumeUsageBasedBillingPlan();
  const zeroDollarPrice = useGetDefaultZeroCost();
  const planInfo = useSelector(Global.selectSellerPlanInfo);
  const { plan = {} } = planInfo ?? {};
  const currentBillingPlan = plan;
  const currentBillingPlanId = parseInt(currentBillingPlan.billingPlanId, 10);
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const defaultCountryCode = useSelector(Global.selectSellerDefaultCurrency) || USD;
  const addOnFeatures = useBuildAddOnFeatures();
  const subscriptionLineItems = getCurrentLineItems(subscriptionData, planInfo);
  const { planToNonWalletAccessPlanMap = {}, allPlans = [] } = subscriptionData ?? {};
  const pendingSubscriptionData = useGetPendingSubscriptionChangeData();

  const basePlan = isByoaVisible
    ? getBasePlanForNonWalletAccessPlan(currentBillingPlan, planToNonWalletAccessPlanMap, allPlans)
    : currentBillingPlan;
  const currentSubscription = subscriptionData?.currentSubscription;
  const subscriptionPrice = currentSubscription?.pricePerPeriod;
  const catalogPlanPrice = useGetPlanPrice(basePlan);
  const byoaBillingState = useNonWalletAccessBillingState();
  const isByoaFree = byoaBillingState !== NonWalletAccessBillingState.Paid;
  const isByoaFreeDuringTrial = byoaBillingState === NonWalletAccessBillingState.FreeDuringTrial;
  const isByoaIncludedInPlan = isBillingPlanIdUsageBilledHighVolume(currentBillingPlanId) || isByoaFree;
  const byoaDescription = useNonWalletAccessDescription({ selectedPlan: basePlan });
  const perLabelFee = useGetPerLabelFeeForNonWalletAccessIncludedPlan(currentBillingPlanId);
  const fractionDigits = getFractionalCentsDigits(perLabelFee);

  const getByoaAddOnPrice = useCallback(() => {
    if (isByoaIncludedInPlan) {
      return zeroDollarPrice;
    }

    return {
      value: getNonWalletAccessDisplayPrice(basePlan, currentBillingPlan),
      code: currentBillingPlan?.pricePerPeriod?.code || defaultCountryCode,
    };
  }, [basePlan, currentBillingPlan, isByoaIncludedInPlan, zeroDollarPrice, defaultCountryCode]);

  const isOnByoaPlan = Object.values(planToNonWalletAccessPlanMap).some(
    (byoaPlan) => byoaPlan.billingPlanId === currentBillingPlan?.billingPlanId,
  );

  const basePlanPrice = useMemo(
    () =>
      isOnByoaPlan || subscriptionPrice?.value == null
        ? catalogPlanPrice
        : {
            value: subscriptionPrice.value - (isByoaVisible ? getByoaAddOnPrice().value : 0),
            code: subscriptionPrice.code || catalogPlanPrice.code,
          },
    [isOnByoaPlan, subscriptionPrice, catalogPlanPrice, isByoaVisible, getByoaAddOnPrice],
  );

  const { hasUnlimitedUsers, additionalUserCount } = useUserLimitData();
  const userAddOnData = useGetUserSeatsAddOnData();
  const analyticsLicenseData = useAnalyticsLicenseData();
  const hasAnalyticsLicenses = analyticsLicenseData.totalLicenseLimit > 0;
  const getUsersLineItem = useCallback(() => {
    const usersCost = {
      code: userAddOnData.unitCost.code,
      value: userAddOnData.unitCost.value * additionalUserCount,
    };
    const userCountDisplay = hasUnlimitedUsers ? <FormattedMessage {...messages.unlimited} /> : additionalUserCount;

    return {
      lineItemId: USER_COST_LINE_ITEM_ID,
      label: <FormattedMessage {...messages.usersLineItem} values={{ count: userCountDisplay }} />,
      price: usersCost,
    };
  }, [hasUnlimitedUsers, additionalUserCount, userAddOnData]);

  const shipmentLimitPrice = useShipmentLimitCostForBillingPlan(currentBillingPlan?.billingPlanId, planInfo?.plan?.shipmentLimit?.limit);

  const lineItemsWithCosts = useMemo(() => {
    /* Base Plan Cost */
    const basePlanCostLineItem = {
      lineItemId: BASE_PLAN_LINE_ITEM_ID,
      price: basePlanPrice,
    };
    /* Additional Users Cost */
    const users = getUsersLineItem();

    /* Shipment Limit Cost */
    const shipmentLimitLineItem = {
      lineItemId: SHIPMENT_LIMIT_LINE_ITEM_ID,
      price: shipmentLimitPrice || zeroDollarPrice,
    };

    /* Analytics Licenses Cost */
    const analyticsLicenseLineItem = {
      lineItemId: ANALYTICS_LICENSE_COST_LINE_ITEM_ID,
      price: hasAnalyticsLicenses ? analyticsLicenseData.additionalLicensesCost : zeroDollarPrice,
    };

    /* Add-Ons Cost — skip NonWalletProviderAccess; it's rendered as a dedicated byoaCost line below */
    const addOnlineItems = subscriptionLineItems.reduce((acc, lineItem) => {
      if (lineItem.featureIds?.includes(Features.NonWalletProviderAccess)) {
        return acc;
      }
      const lineItemCode = lineItem.addOnCode || lineItem.featureIds[0];
      const hasItForFree = lineItem.isIncludedInPlan || lineItem.isTrial || lineItem.isAlphaUser;
      acc.push({
        lineItemId: lineItemCode,
        price: hasItForFree ? zeroDollarPrice : (addOnFeatures.find((addOn) => addOn.addOnCode === lineItemCode)?.price ?? zeroDollarPrice),
      });

      return acc;
    }, []);

    if (!isByoaVisible) {
      return [basePlanCostLineItem, users, analyticsLicenseLineItem, shipmentLimitLineItem, ...addOnlineItems];
    }

    /* BYOA Cost */
    const byoaCost = {
      lineItemId: YOUR_CARRIER_COST_LINE_ITEM_ID,
      price: getByoaAddOnPrice(),
    };

    return [basePlanCostLineItem, users, analyticsLicenseLineItem, shipmentLimitLineItem, byoaCost, ...addOnlineItems];
  }, [
    addOnFeatures,
    analyticsLicenseData,
    basePlanPrice,
    getByoaAddOnPrice,
    getUsersLineItem,
    hasAnalyticsLicenses,
    isByoaVisible,
    shipmentLimitPrice,
    subscriptionLineItems,
    zeroDollarPrice,
  ]);

  return (
    <FlexColumn className={styles.container}>
      <FlexRow className={styles.top}>
        <Text
          className={styles['subscription-header']}
          element='p'
          id='subscription-card-plan-info-header'
          textType={TextType.H3}
          value={messages.newSubscriptionCardHeader}
        />
        <SubscriptionCardActions planInfo={planInfo} reactivatePlan={reactivatePlan} subscriptionData={subscriptionData} />
      </FlexRow>
      <SubscriptionCardPlanInfo basePlanPrice={basePlanPrice} planInfo={planInfo} shipmentLimitPrice={shipmentLimitPrice} />
      <SubscriptionCardUserInfo additionalUsersTotalCost={getUsersLineItem()?.price} />
      {hasAnalyticsLicenses && <SubscriptionCardAnalyticsLicenseInfo />}
      {isByoaVisible && (
        <>
          <FlexRow className={styles['plan-info']}>
            <div className={styles['byoa-info']}>
              <Text textType={TextType.H4} value={messages.yourCarriers} />
              {pendingSubscriptionData && pendingSubscriptionData.isLosingByoaAccess && (
                <Pill
                  label={
                    <FormattedMessage
                      {...messages.deactivationDate}
                      values={{
                        deactivationDt: <UserDateTimezoneFormat dateTime={pendingSubscriptionData?.dateTimeUtc} />,
                      }}
                    />
                  }
                  theme='neutral'
                />
              )}
            </div>
            <PlanLineItemCost
              isIncludedInPlan={isByoaIncludedInPlan}
              price={getByoaAddOnPrice()}
              shouldShowAsterisk={isByoaIncludedInSellersPlan}
            />
          </FlexRow>
          {isByoaFreeDuringTrial && (
            <Text className={styles['byoa-description']} textType={TextType.Caption} element='p' value={byoaDescription} />
          )}
        </>
      )}
      {subscriptionLineItems.length > 0 && <SubscriptionCardAddOnsAndFeatures lineItems={subscriptionLineItems} />}
      <div className={styles['total-cost-container']}>
        <PlanTotalCost lineItems={lineItemsWithCosts} />
      </div>
      {isByoaIncludedInSellersPlan && (
        <Text
          className={styles['price-per-label-footnote']}
          formattedMessageValues={{
            fee: (
              <FormattedCurrencyContainer
                currencyCode={currentBillingPlan?.pricePerPeriod?.code || defaultCountryCode}
                fractionDigits={fractionDigits}
                value={perLabelFee}
              />
            ),
          }}
          textType={TextType.Caption}
          value={messages.additionalFeeForBYOA}
        />
      )}
    </FlexColumn>
  );
};

SubscriptionCard.propTypes = {
  reactivatePlan: PropTypes.func.isRequired,
};

export default SubscriptionCard;
